import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/Button";
import { api } from "@/lib/api";
import { cn } from "@/lib/formatters";

interface CarouselReviewProps {
  slug: string;
  onClose: () => void;
}

/**
 * El carrusel renderizado, para aprobarlo o rechazarlo.
 *
 * Riel horizontal con scroll-snap y no una grilla: un carrusel se lee slide
 * por slide, en orden, como lo va a leer quien lo vea en Instagram. Una grilla
 * de ocho miniaturas muestra las mismas imágenes y esconde justamente lo que
 * hay que juzgar — si la secuencia se sostiene.
 *
 * Los dos botones son finales y no hay regeneración: aprobar deja el carrusel
 * listo para subir, rechazar lo mata. Por eso los dos piden confirmación —
 * escribir la decisión es un movimiento que no tiene vuelta desde acá.
 */
export function CarouselReview({ slug, onClose }: CarouselReviewProps) {
  const qc = useQueryClient();
  const railRef = useRef<HTMLDivElement>(null);
  const [confirmar, setConfirmar] = useState<"approve" | "reject" | null>(null);
  const [visible, setVisible] = useState(1);

  const q = useQuery({
    queryKey: ["marketing-carousel", slug],
    queryFn: () => api.marketing.carousel(slug),
  });

  const decidir = useMutation({
    mutationFn: (decision: "approve" | "reject") =>
      api.marketing.decideCarousel(slug, decision),
    onSuccess: () => {
      setConfirmar(null);
      qc.invalidateQueries({ queryKey: ["marketing-carousel", slug] });
    },
  });

  // Escape cierra. Las flechas mueven el riel un slide: con ocho de 1080px de
  // alto, arrastrar con el mouse es la peor forma de recorrerlo.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (confirmar) setConfirmar(null);
        else onClose();
        return;
      }
      const rail = railRef.current;
      if (!rail) return;
      if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
        e.preventDefault();
        const paso = rail.clientWidth * 0.8 * (e.key === "ArrowRight" ? 1 : -1);
        rail.scrollBy({ left: paso, behavior: "smooth" });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [confirmar, onClose]);

  const data = q.data;
  const review = data?.review ?? null;
  const total = data?.slides.length ?? 0;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      role="dialog"
      aria-modal="true"
      aria-label={`Carrusel de ${slug}`}
      className="fixed inset-0 z-50 bg-bg/92 backdrop-blur-sm flex flex-col"
      onClick={(e) => {
        // Sólo el fondo cierra. Un click dentro del riel es scroll, no salida.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <header className="shrink-0 flex items-center gap-4 px-5 py-3 border-b border-border/60">
        <div className="min-w-0">
          <p className="path-chip">carrusel</p>
          <h2 className="text-sm font-medium truncate mt-1">{slug}</h2>
        </div>
        <div className="ml-auto flex items-center gap-4 font-mono text-[10px] text-muted shrink-0">
          {data && (
            <>
              <span className="uppercase tracking-wider">{data.style}</span>
              <span className="tabular-nums">
                {visible}/{total}
              </span>
              <span className="tabular-nums hidden sm:inline">
                {Math.round(data.bytes / 1024)} KB
              </span>
            </>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="text-muted hover:text-text text-lg leading-none px-1"
          >
            ✕
          </button>
        </div>
      </header>

      {q.isLoading && (
        <div className="flex-1 flex items-center justify-center">
          <p className="font-mono text-sm text-muted animate-pulse">
            $ cargando slides…
          </p>
        </div>
      )}

      {q.isError && (
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="surface p-6 max-w-md text-center space-y-2">
            <p className="text-sm text-danger">No hay carrusel renderizado.</p>
            <p className="text-xs text-muted leading-relaxed">
              El agente sólo renderiza cuando el triage elige el formato carrusel
              y el juez lo deja pasar. Si la corrida es anterior a que el
              workflow renderizara, no hay previews que mostrar.
            </p>
          </div>
        </div>
      )}

      {data && (
        <div
          ref={railRef}
          onScroll={(e) => {
            // Qué slide se está mirando, para el contador del header.
            const el = e.currentTarget;
            const primera = el.firstElementChild as HTMLElement | null;
            if (!primera) return;
            const ancho = primera.offsetWidth + 16; // + gap-4
            setVisible(Math.min(total, Math.round(el.scrollLeft / ancho) + 1));
          }}
          className="flex-1 flex items-center gap-4 overflow-x-auto overflow-y-hidden
                     snap-x snap-mandatory px-5 py-5 scroll-smooth"
        >
          {data.slides.map((s) => (
            <figure key={s.name} className="snap-center shrink-0">
              {/* La altura se ata al viewport y no al riel: con `items-center`
                  la línea flex no estira, así que un `h-full` acá se resuelve
                  contra una altura automática y la imagen colapsa. 10rem es
                  el header más el footer. El tope de ancho es para el celular
                  acostado, donde 4:5 a pantalla completa se sale de lado. */}
              <img
                src={s.src}
                alt={`Slide ${s.n} de ${total}`}
                className="max-h-[calc(100vh-10rem)] max-w-[85vw] w-auto object-contain
                           rounded-lg border border-border/60 shadow-2xl"
              />
            </figure>
          ))}
        </div>
      )}

      <footer className="shrink-0 border-t border-border/60 px-5 py-3">
        {review ? (
          <div className="flex items-center gap-3 flex-wrap">
            <span
              className={cn(
                "font-mono text-xs uppercase tracking-wider px-2.5 py-1 rounded border",
                review.status === "approved"
                  ? "border-success/50 bg-success/10 text-success"
                  : "border-danger/40 bg-danger/10 text-danger",
              )}
            >
              {review.status === "approved" ? "aprobado" : "rechazado"}
            </span>
            <span className="font-mono text-[10px] text-muted">
              {review.decided_by}
              {review.decided_at &&
                ` · ${new Date(review.decided_at).toLocaleString("es")}`}
            </span>
            {review.status === "approved" && (
              <span className="font-mono text-[10px] text-muted ml-auto">
                los PNG a tamaño real están en el artefacto de la corrida
              </span>
            )}
          </div>
        ) : (
          <AnimatePresence mode="wait">
            {confirmar ? (
              <motion.div
                key="confirmar"
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="flex items-center gap-3 flex-wrap"
              >
                <p className="text-xs text-muted">
                  {confirmar === "approve"
                    ? "Queda listo para subir. No se puede deshacer desde acá."
                    : "El carrusel se cancela y no se regenera."}
                </p>
                <div className="ml-auto flex gap-2">
                  <Button
                    variant="ghost"
                    onClick={() => setConfirmar(null)}
                    disabled={decidir.isPending}
                  >
                    Cancelar
                  </Button>
                  <Button
                    variant={confirmar === "approve" ? "primary" : "danger"}
                    onClick={() => decidir.mutate(confirmar)}
                    disabled={decidir.isPending}
                  >
                    {decidir.isPending
                      ? "…"
                      : confirmar === "approve"
                        ? "Sí, aprobar"
                        : "Sí, rechazar"}
                  </Button>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="decidir"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex items-center gap-3 flex-wrap"
              >
                <p className="font-mono text-[10px] text-muted">
                  ← → para recorrer · esc para cerrar
                </p>
                <div className="ml-auto flex gap-2">
                  <Button
                    variant="danger"
                    onClick={() => setConfirmar("reject")}
                    disabled={!data}
                  >
                    Rechazar
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => setConfirmar("approve")}
                    disabled={!data}
                  >
                    Aprobar
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        )}

        {decidir.isError && (
          <p className="mt-2 text-xs text-danger">
            No se pudo guardar: {String(decidir.error)}
          </p>
        )}
      </footer>
    </motion.div>
  );
}
